import React, { useEffect, useMemo, useState } from 'react';
import { Modal, message } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as CloseSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { queryClient } from 'src/query-client';
import { useTypedSelector } from '@hooks';
import { useAuth } from '@hooks/useAuth';
import { decisions } from '@components/MediaViewerHeader/components/DropdownDecisions/utils';
import {
  useDecisionApprovalWorkflow,
  useGetApprovalWorkflow
} from '@hooks/queries/approval';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { SubmitDecisionDto } from '@api/Api';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';

export function useAssetApprovalDecision(password?: string | null) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemoOrganization = useIsDemoOrganization();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const isInternalMediaViewer = location.pathname.includes('media-viewer');
  const isShareReviewLink = location.pathname.includes('external-reviewer');

  const { data: approvalWorkflow, refetch: refetchApprovalWorkflow } =
    useGetApprovalWorkflow({
      enabled: !isShareReviewLink,
      params: {
        id: assets?.asset?.approvalWorkflow?.id || '',
        password
      }
    });

  const { mutateAsync: decisionApprovalWorkflow } = useDecisionApprovalWorkflow(
    {}
  );

  const aw = approvalWorkflow?.approvalWorkflow;
  const awAssets = aw?.assets || [];

  const initialDecision = useMemo(() => {
    if (
      !aw?.stages ||
      !aw?.currentStageId ||
      !user?.email ||
      !selectedVersion?.id
    )
      return null;

    const currentStage = aw.stages.find(
      (stage) => stage?.id === aw.currentStageId
    );
    if (!currentStage) return null;

    const currentMember = currentStage.members?.find(
      (member) => member?.email === user.email
    );
    if (!currentMember) return null;

    const memberDecision = currentMember.decisions?.find(
      (item) => item.assetVersionId === selectedVersion.id
    );
    return memberDecision?.decision || null;
  }, [selectedVersion?.id, aw?.currentStageId, aw?.stages, user?.email]);

  const filteredApprovalListAssets = useMemo(() => {
    const _approvalReviewInBatch = awAssets.filter((item) => !item.decision);
    return sortedAWListAssetsToReviewInBatch(_approvalReviewInBatch);
  }, [awAssets]);

  const [decision, setDecision] = useState(initialDecision);

  useEffect(() => {
    setDecision(initialDecision);
  }, [initialDecision, aw?.currentStageId]);

  const decisionLabel = useMemo(
    () =>
      decisions.find((x) => x.key === decision)?.title ?? 'Select a decision',
    [decision]
  );

  const isCurrentVersion =
    selectedVersion?.versionNumber === assets?.asset?.versionsCount;

  const disableReason = useMemo(() => {
    if (isDemoOrganization) return null;
    if (aw?.isPaused)
      return 'Approval is currently paused. You will be notified when it is resumed.';
    if (aw?.isFinished)
      return 'Approval is already finished. Contact the approval manager if you need to change your decision.';
    if (aw?.lockDecisions && decision)
      return 'Your decision is locked. Contact the approval manager if you need to change it.';
    if (!aw?.permissions.submitDecision)
      return 'Your decision is locked. Contact the approval manager if you need to change it.';
    if (!isCurrentVersion) {
      return 'Approval routing for this version is locked. Please review the latest version to submit your decision.';
    }
    return null;
  }, [
    aw?.isPaused,
    aw?.isFinished,
    aw?.lockDecisions,
    aw?.permissions.submitDecision,
    isDemoOrganization,
    decision,
    isCurrentVersion
  ]);

  const isButtonDisabled = !!disableReason && !isDemoOrganization;

  const refetchAsset = async (assetId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['assets', 'item', assetId]
    });
  };

  const selectDecision = async (
    decisionKey: SubmitDecisionDto['decision'],
    confirmed = false
  ) => {
    if (!assets || !selectedVersion || !aw?.currentStageId) return;
    const stages = aw?.stages;
    const currentStage = stages?.find((el) => el.id === aw.currentStageId);
    if (!currentStage) return;
    setDecision(decisionKey);
    const isLastReviewer = !currentStage.members.some(
      (x) =>
        x.email !== user?.email &&
        x.canSubmitDecision &&
        !(x?.decisions || []).find(
          (i) => i.assetVersionId === selectedVersion.id
        )?.decision
    );
    const needConfirm =
      !confirmed &&
      (!!aw?.lockDecisions ||
        (isLastReviewer && decisionKey !== 'changes_required'));
    if (needConfirm) {
      Modal.confirm({
        onCancel: () => setDecision(initialDecision),
        onOk: () => selectDecision(decisionKey, true),
        width: 500,
        icon: null,
        centered: true,
        okText: 'Yes, submit',
        cancelText: 'No, continue review',
        closable: true,
        maskClosable: true,
        closeIcon: <CloseSvg />,
        content: (
          <div className="modal_container">
            <h2
              className="modal_container__title"
              style={{ fontSize: 22, marginBottom: '0.5em' }}
            >
              Is this your final decision?
            </h2>
            <p className="modal_container__subtitle">
              {isLastReviewer && <>You are the last reviewer in this stage. </>}
              Once submitted, you will not be able to leave additional comments
              or change your decision.
            </p>
          </div>
        )
      });
      return;
    }
    await decisionApprovalWorkflow({
      id: aw.id,
      versionIds: [selectedVersion?.id],
      stageId: aw.currentStageId!,
      password,
      decision: decisionKey
    });
    const submittedLabel = decisions.find((x) => x.key === decisionKey)?.title;
    message.success({
      content: `${currentStage?.name} has been marked as ${submittedLabel}`,
      className: 'message-dark-modal',
      icon: <SuccessSvg className="anticon" />,
      duration: 2
    });
    refetchApprovalWorkflow();
    if (filteredApprovalListAssets.length > 1) {
      const findVersionIndex = filteredApprovalListAssets.findIndex(
        (item) => item.versionId === selectedVersion?.id
      );
      const nextAsset =
        filteredApprovalListAssets?.[findVersionIndex + 1] ||
        filteredApprovalListAssets?.[findVersionIndex - 1];
      if (!nextAsset?.id) {
        await refetchAsset(assets.id);
        return;
      }
      if (isInternalMediaViewer && nextAsset.hasAccess) {
        navigate(`/media-viewer/${nextAsset.id}/${nextAsset.versionId}`, {
          replace: true
        });
        return;
      }
      if (isInternalMediaViewer && !nextAsset.hasAccess) {
        navigate(`/approval?id=${aw.id}&assetId=${nextAsset.id}`, {
          replace: true
        });
        return;
      }
      searchParams.set('assetId', nextAsset.id);
      navigate({ search: searchParams.toString() }, { replace: true });
    } else {
      await refetchAsset(assets.id);
    }
  };

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.id,
    () => refetchApprovalWorkflow(),
    1000,
    (msg) => {
      if (msg.payload.id !== aw?.id) return false;
      return !msg.causedByMe;
    },
    'aw-updated'
  );

  const showDecision = !!aw?.permissions.submitDecision || !!decision;

  return {
    aw,
    assets,
    selectedVersion,
    decision,
    decisionLabel,
    disableReason,
    filteredApprovalListAssets,
    isButtonDisabled,
    isDemoOrganization,
    isShareReviewLink,
    showDecision,
    selectDecision
  };
}
